/**
 * /recruitment/candidates — candidate list + create (ADR-0012). Candidates are
 * a distinct PII entity (D-1); consent is captured at create (D-9). Links to a
 * candidate's profile (CV, applications, offers) and to the CSV bulk import.
 *
 * HR-gated; mounted under the admin layout.
 */
import { createRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import { createCandidate, listCandidates, type Candidate } from '../api/recruitment.js';
import { ApiErrorNotice } from '../components/ApiErrorNotice.js';
import { formatDate } from '../lib/format.js';

export const recruitmentCandidatesRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/recruitment/candidates',
  component: RecruitmentCandidatesPage,
});

const COUNTRIES = ['NG', 'GH', 'CD'] as const;

function RecruitmentCandidatesPage(): JSX.Element {
  const query = useQuery({ queryKey: ['recruitment', 'candidates'], queryFn: () => listCandidates() });

  return (
    <section className="p-8 space-y-6 max-w-4xl">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <h1 className="font-serif text-3xl">Candidates</h1>
          <p className="text-sm text-gray-400">People in the hiring funnel. Consent is recorded when they’re added.</p>
        </div>
        <Link to="/recruitment/import" className="rounded border border-gray-700 text-sm px-3 py-2 hover:bg-gray-900 text-gray-200">
          Bulk import →
        </Link>
      </header>

      <CreateForm />

      {query.isLoading ? (
        <Placeholder>Loading…</Placeholder>
      ) : query.isError ? (
        <Placeholder error>Could not load candidates.</Placeholder>
      ) : (query.data?.candidates ?? []).length === 0 ? (
        <Placeholder>No candidates yet.</Placeholder>
      ) : (
        <div className="space-y-2">
          {query.data!.candidates.map((c) => (
            <CandidateRow key={c.id} candidate={c} />
          ))}
        </div>
      )}
    </section>
  );
}

function CandidateRow({ candidate }: { candidate: Candidate }): JSX.Element {
  const anonymized = candidate.anonymizedAt != null;
  return (
    <Link
      to="/recruitment/candidates/$candidateId"
      params={{ candidateId: candidate.id }}
      className="block rounded border border-gray-900 p-4 hover:bg-gray-950"
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm text-white">
            {candidate.firstName} {candidate.lastName}
            <span className="text-gray-500 text-xs ml-2">{candidate.countryCode}</span>
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {candidate.email ?? 'no email'} · added {formatDate(candidate.createdAt)}
          </p>
        </div>
        <div className="flex gap-2">
          {anonymized ? (
            <span className="rounded px-2 py-1 text-xs border border-gray-700 text-gray-500">Anonymised</span>
          ) : candidate.consentGivenAt ? (
            <span className="rounded px-2 py-1 text-xs border border-green-900 text-green-400">Consent on file</span>
          ) : (
            <span className="rounded px-2 py-1 text-xs border border-yellow-900 text-yellow-400">No consent</span>
          )}
        </div>
      </div>
    </Link>
  );
}

function CreateForm(): JSX.Element {
  const queryClient = useQueryClient();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [countryCode, setCountryCode] = useState<(typeof COUNTRIES)[number]>('NG');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [source, setSource] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);

  const mut = useMutation({
    mutationFn: () =>
      createCandidate({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        countryCode,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        source: source.trim() || undefined,
        consentGiven,
      }),
    onSuccess: async () => {
      setFirstName(''); setLastName(''); setEmail(''); setPhone(''); setSource(''); setConsentGiven(false);
      await queryClient.invalidateQueries({ queryKey: ['recruitment', 'candidates'] });
    },
  });

  return (
    <form
      className="rounded border border-gray-900 p-5 space-y-4"
      onSubmit={(e) => { e.preventDefault(); if (firstName.trim() && lastName.trim()) mut.mutate(); }}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-sm text-gray-300 space-y-1"><span>First name</span>
          <input value={firstName} onChange={(e) => setFirstName(e.target.value)} aria-label="First name" className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
        </label>
        <label className="text-sm text-gray-300 space-y-1"><span>Last name</span>
          <input value={lastName} onChange={(e) => setLastName(e.target.value)} aria-label="Last name" className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
        </label>
        <label className="text-sm text-gray-300 space-y-1"><span>Country</span>
          <select value={countryCode} onChange={(e) => setCountryCode(e.target.value as (typeof COUNTRIES)[number])} aria-label="Country" className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm">
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="text-sm text-gray-300 space-y-1"><span>Email</span>
          <input value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email" className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
        </label>
        <label className="text-sm text-gray-300 space-y-1"><span>Phone</span>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} aria-label="Phone" className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
        </label>
        <label className="text-sm text-gray-300 space-y-1"><span>Source</span>
          <input value={source} onChange={(e) => setSource(e.target.value)} aria-label="Source" placeholder="Referral / LinkedIn / …" className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
        </label>
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-300">
        <input type="checkbox" checked={consentGiven} onChange={(e) => setConsentGiven(e.target.checked)} aria-label="Consent given" />
        Consent to store this person’s details is on file
      </label>
      <button type="submit" disabled={!firstName.trim() || !lastName.trim() || mut.isPending} className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200 disabled:opacity-50">
        {mut.isPending ? 'Adding…' : 'Add candidate'}
      </button>
      <ApiErrorNotice error={mut.error} fallback="Could not add the candidate." />
    </form>
  );
}

function Placeholder({ children, error }: { children: React.ReactNode; error?: boolean }): JSX.Element {
  return (
    <div className={`rounded border p-6 text-center text-sm ${error ? 'border-red-900 bg-red-950/40 text-red-300' : 'border-gray-900 text-gray-500'}`}>
      {children}
    </div>
  );
}
